import * as THREE from 'three';
import { GAME_STATES, PLANET } from '../utils/constants.js';
import { dispose } from '../utils/math.js';
import { events, EVT } from '../utils/events.js';
import { GameState } from '../player/GameState.js';

export class PlanetStreamer {
  constructor(game) {
    this.game = game;
  }

  async _loadInitialSystem() {
    // ── Restore most recently saved planet from Firebase ──
    const remote = this.game.gameState.remoteLastPlanet;
    if (remote?.position) {
      const { gx, gy, planetIndex } = remote.position;
      const savedSystem = this.game.galaxy.getSystem(gx, gy);
      if (savedSystem) {
        console.log(`[Game] Restoring Firebase planet: "${remote.name}" in ${savedSystem.name}`);
        await this._enterSystem(savedSystem, planetIndex ?? 0);
        return;
      }
    }

    // ── Single-planet mode (fallback) ──
    // Always load the same deterministic system/planet (seed 42, first system)
    let system = null;
    for (let r = 0; r < 20 && !system; r++) {
      for (let x = -r; x <= r && !system; x++) {
        for (let y = -r; y <= r && !system; y++) {
          system = this.game.galaxy.getSystem(x, y);
        }
      }
    }

    if (!system) {
      console.error('No system found!');
      return;
    }

    // Load the first planet — this is THE planet for the whole experience
    await this._enterSystem(system, 0);
  }

  async _enterSystem(system, planetIndex = 0) {
    // Clear old
    this._clearPlanetScene();

    this.game.currentSystem = system;
    this.game.currentPlanet = system.planets[planetIndex] || system.planets[0];
    this.game.focusedPlanet = this.game.currentPlanet;
    this.game.gameState.setLocation(system.gx, system.gy, planetIndex);

    // ── Star ──
    const starGeo = new THREE.SphereGeometry(system.star.size * 15, 32, 32);
    const starMat = new THREE.MeshStandardMaterial({
      color: system.star.color,
      emissive: system.star.color,
      emissiveIntensity: 2,
    });
    this.game.currentStarMesh = new THREE.Mesh(starGeo, starMat);
    this.game.currentStarMesh.position.set(300, 100, -200);
    this.game.scene.add(this.game.currentStarMesh);

    // Update sun light color
    this.game.sunLight.color.setHex(system.star.color);

    // ── Active planet at origin (full terrain) ──
    this.game.activePlanetWorldPos.set(0, 0, 0);
    this._loadActivePlanet(this.game.currentPlanet);

    // ── Place other planets at orbital distances ──
    this._buildSystemPlanetMarkers();

    // Update UI
    const claimName = this.game.gameState.getPlanetClaimName(system.gx, system.gy, this.game.currentPlanet.index);
    const displayPlanetName = claimName || this.game.currentPlanet.name;
    this.game.ui.updateLocation(system.name, displayPlanetName);

    console.log(`[Game] Entered system ${system.name}, planet ${this.game.currentPlanet.name}`);
  }

  /**
   * Load a planet's full terrain, creatures, and ship placement at the origin.
   */
  _loadActivePlanet(planet) {
    // Remove previous terrain + creatures (but not system markers)
    if (this.game.currentPlanetGroup) {
      dispose(this.game.currentPlanetGroup);
      this.game.scene.remove(this.game.currentPlanetGroup);
    }
    for (const mesh of this.game.creatureMeshes) {
      dispose(mesh);
      this.game.scene.remove(mesh);
    }
    this.game.creatureMeshes = [];
    this.game.creatureData = [];

    // Clear rock formations / surface scatter from previous planet
    this.game.rockFormation.clear();
    this.game.surfaceScatter.clear();

    this.game.currentPlanet = planet;

    // ── Planet terrain mesh ──
    const planetDetail = this._getPlanetMeshDetail(false);
    this.game.currentPlanetGroup = this.game.planetRenderer.createPlanetMesh(planet, planetDetail);
    this.game.currentPlanetGroup.position.copy(this.game.activePlanetWorldPos);
    this.game.scene.add(this.game.currentPlanetGroup);
    this.game.activePlanetDetailLevel = planetDetail;

    // ── Place ship on surface ──
    this.game.spawnDir = new THREE.Vector3(0, 1, 0).normalize();
    const shipSurface = this.game.planetRenderer.getSurfacePosition(planet, this.game.spawnDir);
    shipSurface.add(this.game.activePlanetWorldPos);
    this.game.shipModel.group.position.copy(shipSurface).addScaledVector(this.game.spawnDir, 2);

    // Orient ship so its "up" faces away from planet center
    const shipUp = this.game.spawnDir.clone();
    const shipLook = new THREE.Vector3(1, 0, 0);
    shipLook.sub(shipUp.clone().multiplyScalar(shipLook.dot(shipUp))).normalize();
    const shipMatrix = new THREE.Matrix4().makeBasis(
      shipLook,
      shipUp,
      new THREE.Vector3().crossVectors(shipLook, shipUp).normalize()
    );
    this.game.shipModel.group.setRotationFromMatrix(shipMatrix);

    this.game.shipController.isLanded = true;
    this.game.shipController.isFlying = false;

    // ── Which song lives on THIS planet? ──
    // Home planet: the chain song you received on arrival. Singing stars:
    // that traveler's own composition. Everywhere else stays procedural
    // wilderness — every human planet sounds like its human.
    const gs = this.game.gameState;
    const singing = this.game.currentSystem
      ? gs.getSingingSong(this.game.currentSystem.gx, this.game.currentSystem.gy, planet.index)
      : null;
    const isHome = gs.homePlanetSeed == null || planet.seed === gs.homePlanetSeed;

    let songData = null;
    let pm = null;
    if (singing) {
      songData = singing.songData;
      pm = singing.planetMusic;
      const line = GameState.statsLine(singing.stats);
      events.emit(EVT.NOTIFICATION,
        `Transmission source: “${singing.songName}” — ${singing.playerName}${line ? ` · ${line}` : ''}`);
    } else if (isHome) {
      songData = gs.previousSongData;
      pm = gs.previousPlanetMusic;
    }
    this._activeSongData = songData;

    if (songData?.tracks?.length) {
      if (pm) {
        // Restore full planet music config saved with the composition
        Object.assign(planet.music, pm);
      } else {
        // Fallback: override key/scale/tempo from song data
        if (songData.key) planet.music.root = songData.key;
        if (songData.scaleName) planet.music.scale = songData.scaleName;
        if (songData.bpm) planet.music.tempo = songData.bpm;
      }
    }

    // ── Generate creatures (from the planet's song when it has one) ──
    const allCreatures = songData?.tracks?.length
      ? this.game.creatureGen.generateCreaturesFromSong(planet.seed, songData, planet.visual?.hue)
      : this.game.creatureGen.generateCreatures(planet.seed, planet.numCreatures, planet.music?.genre, planet.visual?.hue);
    allCreatures.forEach(c => {
      if (this.game.gameState.isCreatureCollected(c.id)) c.collected = true;
    });

    // Build parallel arrays: only active (non-collected) creatures get meshes.
    // creatureData[i] ↔ creatureMeshes[i] are always 1-to-1.
    this.game.creatureData = [];
    this.game.creatureMeshes = [];
    this.game._allCreatureData = allCreatures; // keep full list for reference

    allCreatures.forEach((creature) => {
      if (creature.collected) return;
      const mesh = this.game.creatureGen.createCreatureMesh(creature);

      // Spawn in cluster — use the creature's pre-computed spawn angles
      const theta = creature.spawnTheta ?? (Math.random() * Math.PI * 2);
      const phi = creature.spawnPhi ?? (0.3 + Math.random() * 2.0);
      const dir = new THREE.Vector3(
        Math.sin(phi) * Math.cos(theta),
        Math.cos(phi),
        Math.sin(phi) * Math.sin(theta)
      ).normalize();

      const surfacePos = this.game.planetRenderer.getSurfacePosition(planet, dir);
      surfacePos.add(this.game.activePlanetWorldPos);
      mesh.position.copy(surfacePos).addScaledVector(dir, creature.size * 0.3);

      const outward = dir.clone();
      mesh.up.copy(outward);
      const tangent = new THREE.Vector3(0, 1, 0).cross(outward).normalize();
      if (tangent.lengthSq() < 0.001) tangent.set(1, 0, 0);
      mesh.lookAt(mesh.position.clone().add(tangent));

      this.game.scene.add(mesh);
      creature._meshRef = mesh; // reference for spatial audio
      this.game.creatureData.push(creature);
      this.game.creatureMeshes.push(mesh);
    });

    // Keep flocking/call-response bound to the currently active planet data.
    this._syncActivePlanetFlocking();
  }

  _syncActivePlanetFlocking() {
    if (!this.game.currentPlanet) return;

    const flockRadius = PLANET.SURFACE_RADIUS * this.game.currentPlanet.size;
    const flockHeightFn = (dir) => this.game.planetRenderer.getTerrainHeight(
      this.game.currentPlanet.terrain, this.game.currentPlanet.seed, flockRadius, dir,
    );

    this.game.flockingSystem.setPlanet(
      new THREE.Vector3(0, 0, 0),
      flockRadius,
      flockHeightFn,
      this.game.currentPlanet.music.tempo,
    );
    this.game.flockingSystem.buildFlocks(this.game.creatureData, this.game.creatureMeshes);
    this.game.flockingSystem.setHarmony(this.game.audio.harmony);
    // Give the flocks THIS planet's literal composition so the broadcast
    // reveal plays its author's actual notes, not procedural approximations.
    // Planet octave is the base-octave fallback for songs saved before the
    // serializer included it.
    this.game.flockingSystem.setSong(
      this._activeSongData || null,
      this.game.currentPlanet?.music?.octave || 3,
    );
  }

  /**
   * Build simple sphere markers for all non-active planets in the system
   * so the player can see and fly to them.
   */
  _getPlanetMeshDetail(isDistant) {
    return this.game._isMobile ? (isDistant ? 10 : 32) : (isDistant ? 14 : 64);
  }

  _setActivePlanetDetail(isDistant) {
    const desiredDetail = this._getPlanetMeshDetail(isDistant);
    if (this.game.activePlanetDetailLevel === desiredDetail) return;
    if (!this.game.currentPlanet) return;

    if (this.game.currentPlanetGroup) {
      dispose(this.game.currentPlanetGroup);
      this.game.scene.remove(this.game.currentPlanetGroup);
    }

    this.game.currentPlanetGroup = this.game.planetRenderer.createPlanetMesh(this.game.currentPlanet, desiredDetail);
    this.game.currentPlanetGroup.position.copy(this.game.activePlanetWorldPos);
    this.game.scene.add(this.game.currentPlanetGroup);
    this.game.activePlanetDetailLevel = desiredDetail;
  }

  _setFocusedPlanet(planet) {
    if (!planet || this.game.focusedPlanet === planet) return;
    this.game.focusedPlanet = planet;
    const claimName = this.game.gameState.getPlanetClaimName(this.game.currentSystem.gx, this.game.currentSystem.gy, planet.index);
    this.game.ui.updateLocation(this.game.currentSystem.name, claimName || planet.name);
    this._syncFocusedPlanetAudio();
    this._syncFocusedPlanetFlocking();
  }

  _clearFocusedPlanet() {
    if (!this.game.focusedPlanet) return;
    this.game.focusedPlanet = null;
    this.game.ui.updateLocation(this.game.currentSystem.name, 'Silent');
    this.game.ui.updateMusicLabel('Silent');
    this.game.audio.fadeOut(1);
  }

  _getSystemPlanetEntry(planet) {
    return this.game.systemPlanetMeshes.find(entry => entry.planet.index === planet.index);
  }

  _syncFocusedPlanetAudio() {
    if (!this.game.focusedPlanet) return;
    const currentAudioPlanet = this.game.audio.currentPlanet;
    if (currentAudioPlanet === this.game.focusedPlanet) return;

    let creatures;
    if (this.game.focusedPlanet.index === this.game.currentPlanet.index) {
      creatures = this.game.creatureData.filter(c => !c.collected);
    } else {
      const entry = this._getSystemPlanetEntry(this.game.focusedPlanet);
      if (!entry) return;
      this._ensureNearbyPlanetCreatureData(entry);
      creatures = entry.creatureData.filter(c => !c.collected);
    }

    const isFlyingTransition = this.game.state === GAME_STATES.FLYING;
    this.game.audio.startPlanetSymphony(this.game.focusedPlanet, creatures, {
      fadeInDuration: isFlyingTransition ? 2.4 : 1.2,
      fadeOutDuration: isFlyingTransition ? 2.0 : 0.9,
    }).catch(() => {});
  }

  _syncFocusedPlanetFlocking() {
    if (!this.game.focusedPlanet) return;

    let planet = this.game.focusedPlanet;
    let center = this.game.activePlanetWorldPos;
    let creatures = this.game.creatureData;
    let meshes = this.game.creatureMeshes;

    if (this.game.focusedPlanet !== this.game.currentPlanet) {
      const entry = this._getSystemPlanetEntry(this.game.focusedPlanet);
      if (!entry) return;

      // Focused nearby planet must be fully available for audio sequencing.
      this._setPlanetMarkerDetail(entry, 'full');
      this._setNearbyPlanetCreatures(entry, true);
      this._setNearbyPlanetAudio(entry, true);

      planet = entry.planet;
      center = entry.worldPos;
      creatures = entry.creatureData || [];
      meshes = entry.creatureMeshes || [];
    }

    const flockRadius = PLANET.SURFACE_RADIUS * planet.size;
    const flockHeightFn = (dir) => this.game.planetRenderer.getTerrainHeight(
      planet.terrain, planet.seed, flockRadius, dir,
    );

    this.game.flockingSystem.setPlanet(center, flockRadius, flockHeightFn, planet.music.tempo);
    this.game.flockingSystem.buildFlocks(
      creatures.filter(c => !c.collected),
      meshes,
    );
    this.game.flockingSystem.setHarmony(this.game.audio.harmony);
  }

  _createPlanetMarkerMesh(planet) {
    const radius = PLANET.SURFACE_RADIUS * planet.size;
    const geo = new THREE.IcosahedronGeometry(radius, 2);
    const col = new THREE.Color();
    col.setHSL(planet.visual.hue, planet.visual.saturation, planet.visual.lightness);
    const mat = new THREE.MeshStandardMaterial({ color: col, roughness: 0.6, metalness: 0.2 });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.name = `planet-marker-${planet.index}`;

    const glowGeo = new THREE.SphereGeometry(radius * 0.3, 8, 8);
    const glowMat = new THREE.MeshBasicMaterial({ color: col, transparent: false, opacity: 1 });
    const glow = new THREE.Mesh(glowGeo, glowMat);
    glow.position.set(0, radius + 5, 0);
    mesh.add(glow);

    return mesh;
  }

  _createNearbyPlanetMesh(planet, isFull = false) {
    const detail = isFull ? this._getPlanetMeshDetail(false) : (this.game._isMobile ? 24 : 32);
    const group = this.game.planetRenderer.createPlanetMesh(planet, detail);
    group.name = `planet-detail-${planet.index}`;
    return group;
  }

  _ensureNearbyPlanetCreatureData(entry) {
    if (entry.creatureData) return;
    entry.creatureData = this.game.creatureGen.generateCreatures(entry.planet.seed, entry.planet.numCreatures, entry.planet.music?.genre, entry.planet.visual?.hue);
    entry.creatureData.forEach(c => {
      if (this.game.gameState.isCreatureCollected(c.id)) c.collected = true;
    });
  }

  _clearNearbyPlanetCreatureMeshes(entry) {
    if (!entry.creatureMeshes) return;
    entry.creatureMeshes.forEach(mesh => {
      dispose(mesh);
      this.game.scene.remove(mesh);
      const idx = this.game.creatureMeshes.indexOf(mesh);
      if (idx >= 0) this.game.creatureMeshes.splice(idx, 1);
    });
    entry.creatureMeshes = [];
    entry.areCreaturesVisible = false;
  }

  _setNearbyPlanetCreatures(entry, shouldShow) {
    if (entry.areCreaturesVisible === shouldShow) return;
    if (!shouldShow) {
      this._clearNearbyPlanetCreatureMeshes(entry);
      return;
    }

    this._ensureNearbyPlanetCreatureData(entry);
    entry.creatureMeshes = entry.creatureMeshes || [];

    entry.creatureData.forEach((creature, i) => {
      if (creature.collected) return;
      const mesh = this.game.creatureGen.createCreatureMesh(creature);
      const theta = (i / entry.creatureData.length) * Math.PI * 2;
      const phi = 0.3 + (i % 3) * 0.4;
      const dir = new THREE.Vector3(
        Math.sin(phi) * Math.cos(theta),
        Math.cos(phi),
        Math.sin(phi) * Math.sin(theta)
      ).normalize();

      const surfacePos = this.game.planetRenderer.getSurfacePosition(entry.planet, dir);
      surfacePos.add(entry.worldPos);
      mesh.position.copy(surfacePos).addScaledVector(dir, creature.size * 0.3);

      mesh.userData.planetCenter = entry.worldPos.clone();
      surfacePos.sub(entry.worldPos).normalize();
      mesh.up.copy(dir);
      const tangent = new THREE.Vector3(0, 1, 0).cross(dir).normalize();
      if (tangent.lengthSq() < 0.001) tangent.set(1, 0, 0);
      mesh.lookAt(mesh.position.clone().add(tangent));

      this.game.scene.add(mesh);
      this.game.creatureMeshes.push(mesh);
      entry.creatureMeshes.push(mesh);
    });

    entry.areCreaturesVisible = true;
  }

  _setNearbyPlanetAudio(entry, shouldPlay) {
    if (entry.isAudioActive === shouldPlay) return;
    if (!shouldPlay) {
      if (entry.isAudioActive) {
        this.game.audio.stopNearbyPlanetAmbience(entry.planet.index);
      }
      entry.isAudioActive = false;
      return;
    }

    this._ensureNearbyPlanetCreatureData(entry);
    this.game.audio.startNearbyPlanetAmbience(entry.planet, entry.creatureData).catch(() => {});
    entry.isAudioActive = true;
  }

  _setPlanetMarkerDetail(entry, mode = 'marker') {
    if (entry.detailMode === mode) return;

    if (entry.mesh) {
      dispose(entry.mesh);
      this.game.scene.remove(entry.mesh);
    }

    if (mode === 'full') {
      entry.mesh = this._createNearbyPlanetMesh(entry.planet, true);
    } else if (mode === 'nearby') {
      entry.mesh = this._createNearbyPlanetMesh(entry.planet, false);
    } else {
      entry.mesh = this._createPlanetMarkerMesh(entry.planet);
    }

    entry.mesh.position.copy(entry.worldPos);
    this.game.scene.add(entry.mesh);
    entry.detailMode = mode;
  }

  _buildSystemPlanetMarkers() {
    // Remove old markers
    for (const entry of this.game.systemPlanetMeshes) {
      dispose(entry.mesh);
      this.game.scene.remove(entry.mesh);
      this._clearNearbyPlanetCreatureMeshes(entry);
      if (entry.isAudioActive) {
        this.game.audio.stopNearbyPlanetAmbience(entry.planet.index);
      }
    }
    this.game.systemPlanetMeshes = [];

    if (!this.game.currentSystem) return;

    for (const planet of this.game.currentSystem.planets) {
      if (planet.index === this.game.currentPlanet.index) continue; // active planet is already rendered

      const angle = planet.orbitPhase;
      const dist = planet.orbitRadius * 8; // scale up so there's real flying distance
      const worldPos = new THREE.Vector3(
        Math.cos(angle) * dist,
        (planet.index % 2 === 0 ? 1 : -1) * planet.index * 10, // slight vertical spread
        Math.sin(angle) * dist
      );

      const activeAngle = this.game.currentPlanet.orbitPhase;
      const activeDist = this.game.currentPlanet.orbitRadius * 8;
      const activeOrbitalPos = new THREE.Vector3(
        Math.cos(activeAngle) * activeDist,
        0,
        Math.sin(activeAngle) * activeDist
      );
      worldPos.sub(activeOrbitalPos); // now relative to active planet at origin

      const mesh = this._createPlanetMarkerMesh(planet);
      mesh.position.copy(worldPos);
      this.game.scene.add(mesh);

      this.game.systemPlanetMeshes.push({
        mesh,
        planet,
        worldPos,
        detailMode: 'marker',
        areCreaturesVisible: false,
        isAudioActive: false,
        creatureData: null,
        creatureMeshes: [],
      });
    }
  }

  /**
   * Switch the active planet while staying in the system.
   * Moves world so new planet is at origin, rebuilds markers.
   */
  async _switchToPlanet(planet) {
    // Find the marker for this planet to get its world offset
    const entry = this.game.systemPlanetMeshes.find(e => e.planet.index === planet.index);
    if (!entry) return;

    const offset = entry.worldPos.clone(); // how far the new planet was from origin

    // Move everything in the scene by -offset so the new planet ends up at origin
    // (star, ship, markers, creatures all shift)
    this.game.activePlanetWorldPos.set(0, 0, 0);

    // Update current planet
    this.game.currentPlanet = planet;
    this.game.focusedPlanet = this.game.currentPlanet;
    this.game.gameState.setLocation(this.game.currentSystem.gx, this.game.currentSystem.gy, planet.index);

    // Rebuild terrain + creatures at origin
    this._loadActivePlanet(planet);

    // Rebuild markers (they recalculate positions relative to new active planet)
    this._buildSystemPlanetMarkers();

    // Move ship to this planet's surface
    // (ship is already repositioned in _loadActivePlanet)

    // Move the star mesh by the offset
    if (this.game.currentStarMesh) {
      this.game.currentStarMesh.position.sub(offset);
    }

    // Start new planet's music
    const activeCreatures = this.game.creatureData.filter(c => !c.collected);
    this.game.audio.startPlanetSymphony(planet, activeCreatures);

    // Update UI
    const claimName = this.game.gameState.getPlanetClaimName(
      this.game.currentSystem.gx, this.game.currentSystem.gy, planet.index
    );
    this.game.ui.updateLocation(this.game.currentSystem.name, claimName || planet.name);

    console.log(`[Game] Switched to planet ${planet.name}`);
  }

  _clearPlanetScene() {
    if (this.game.currentPlanetGroup) {
      dispose(this.game.currentPlanetGroup);
      this.game.scene.remove(this.game.currentPlanetGroup);
      this.game.currentPlanetGroup = null;
    }
    if (this.game.currentStarMesh) {
      dispose(this.game.currentStarMesh);
      this.game.scene.remove(this.game.currentStarMesh);
      this.game.currentStarMesh = null;
    }
    for (const mesh of this.game.creatureMeshes) {
      dispose(mesh);
      this.game.scene.remove(mesh);
    }
    this.game.creatureMeshes = [];
    this.game.creatureData = [];
    this.game.flockingSystem.clear();
    this.game.surfaceScatter.clear();
    // Clean up system planet markers
    for (const entry of this.game.systemPlanetMeshes) {
      dispose(entry.mesh);
      this.game.scene.remove(entry.mesh);
      this._clearNearbyPlanetCreatureMeshes(entry);
      if (entry.isAudioActive) {
        this.game.audio.stopNearbyPlanetAmbience(entry.planet.index);
      }
    }
    this.game.systemPlanetMeshes = [];
  }
}
